import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, MapPin, Compass, Sparkle, Suitcase, Heart, Share as ShareIcon } from "@phosphor-icons/react";
import { itinerariesApi } from "@/lib/apiClient";
import { PassportLogo } from "@/components/Reveal";
import AIMarkdown from "@/components/AIMarkdown";
import TripCountdown from "@/components/TripCountdown";
import ConciergeChat from "@/components/ConciergeChat";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export default function Itinerary() {
    const { t } = useTranslation();
    const { id } = useParams();
    const { user } = useAuth();
    const [it, setIt] = useState(null);
    const [err, setErr] = useState(null);
    const [editingDate, setEditingDate] = useState(false);
    const [dateInput, setDateInput] = useState("");

    useEffect(() => {
        itinerariesApi.get(id).then(setIt).catch((e) => setErr(e?.response?.data?.detail || t("itinerary.notFound")));
    }, [id, t]);

    const share = async () => {
        try {
            const url = window.location.href;
            if (navigator.share) await navigator.share({ title: it.title, text: it.preview, url });
            else { await navigator.clipboard.writeText(url); toast.success(t("itinerary.toast.linkCopied")); }
        } catch {}
    };

    const saveDate = async () => {
        try {
            const updated = await itinerariesApi.update(id, { trip_date: dateInput ? new Date(dateInput).toISOString() : null });
            setIt((prev) => ({ ...prev, trip_date: updated.trip_date }));
            setEditingDate(false);
            toast.success(dateInput ? t("itinerary.toast.countdownSet") : t("itinerary.toast.tripDateCleared"));
        } catch { toast.error(t("itinerary.toast.couldNotSaveDate")); }
    };

    const isOwner = user && it && user.id === it.user_id;

    if (err) return <NotFound msg={err} t={t} />;
    if (!it) return <div className="min-h-screen grid place-items-center text-white/60">{t("common.loading")}</div>;

    const isTrip = it.kind === "trip";

    return (
        <div className="min-h-screen grain" data-testid="itinerary-page">
            <header className="sticky top-0 z-40 glass border-b" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                <div className="max-w-[900px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
                    <Link to="/app" className="flex items-center gap-3">
                        <PassportLogo size={32} />
                        <div className="hidden sm:block">
                            <div className="font-serif text-lg text-white">{t("conciergeApp.passport")}</div>
                            <div className="overline" style={{ fontSize: "8px" }}>{t("itinerary.headerOverline")}</div>
                        </div>
                    </Link>
                    <div className="flex gap-2">
                        <button onClick={share} className="btn-ghost text-xs !py-2 !px-3" data-testid="itinerary-share">
                            <ShareIcon size={12} /> {t("common.share")}
                        </button>
                        <Link to="/app" className="btn-ghost text-xs !py-2 !px-3">
                            <ArrowLeft size={12} /> {t("auth.back")}
                        </Link>
                    </div>
                </div>
            </header>

            <div className="max-w-[900px] mx-auto px-6 md:px-10 py-10">
                <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="w-10 h-10 rounded-full grid place-items-center" style={{ background: "rgba(212,175,55,0.16)", border: "1px solid rgba(212,175,55,0.4)" }}>
                            {isTrip ? <Suitcase size={16} color="#D4AF37" weight="fill" /> : <Heart size={16} color="#F4A28F" weight="fill" />}
                        </div>
                        <div className="overline" style={{ color: "var(--gold)" }}>
                            {isTrip ? t("itinerary.aiItinerary") : t("nav.traveldate")}
                        </div>
                    </div>
                    <h1 className="font-serif text-4xl md:text-6xl text-white tracking-tighter leading-[0.95] mb-4">
                        {it.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-white/60">
                        {it.destination && (
                            <span className="inline-flex items-center gap-1.5">
                                <MapPin size={12} weight="fill" color="#D4AF37" /> {it.destination}
                            </span>
                        )}
                        {isTrip && it.days && (
                            <span className="inline-flex items-center gap-1.5">
                                <Compass size={12} color="#D4AF37" /> {t("itinerary.days", { count: it.days })}
                            </span>
                        )}
                        {!isTrip && it.duration_label && (
                            <span className="inline-flex items-center gap-1.5">
                                <Compass size={12} color="#D4AF37" /> {it.duration_label}
                            </span>
                        )}
                        {(it.budget || it.budget_usd) && (
                            <span className="inline-flex items-center gap-1.5">
                                <Sparkle size={12} color="#D4AF37" /> {it.budget || `~$${it.budget_usd}`}
                            </span>
                        )}
                        {it.style && (
                            <span className="capitalize">{it.style}</span>
                        )}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-3xl p-8 md:p-10"
                    style={{ background: "linear-gradient(140deg, rgba(212,175,55,0.06), var(--navy-deep))", border: "1px solid rgba(212,175,55,0.2)" }}
                    data-testid="itinerary-content"
                >
                    <AIMarkdown>{it.content}</AIMarkdown>
                </motion.div>

                {/* Trip Countdown */}
                {isOwner && (
                    <div className="mt-6">
                        {it.trip_date ? (
                            <div className="flex items-start gap-3 flex-wrap">
                                <div className="flex-1 min-w-[260px]">
                                    <TripCountdown tripDate={it.trip_date} destination={it.destination} />
                                </div>
                                <button
                                    onClick={() => { setDateInput(it.trip_date?.slice(0, 10) || ""); setEditingDate(true); }}
                                    className="btn-ghost text-xs !py-2 !px-3"
                                    data-testid="itinerary-edit-date"
                                >
                                    {t("itinerary.date.changeDate")}
                                </button>
                            </div>
                        ) : editingDate ? null : (
                            <button
                                onClick={() => setEditingDate(true)}
                                className="w-full rounded-2xl p-4 text-left"
                                style={{ background: "rgba(255,255,255,0.03)", border: "1px dashed rgba(255,255,255,0.15)" }}
                                data-testid="itinerary-add-date"
                            >
                                <div className="text-sm text-white font-medium">{t("itinerary.date.addTripDate")}</div>
                                <div className="text-xs text-white/50 mt-1">{t("itinerary.date.addTripDateSub")}</div>
                            </button>
                        )}
                        {editingDate && (
                            <div className="mt-3 rounded-2xl p-4 flex items-end gap-2" style={{ background: "var(--navy-deep)", border: "1px solid rgba(212,175,55,0.2)" }}>
                                <div className="flex-1">
                                    <div className="overline mb-2">{t("itinerary.date.tripDateOverline")}</div>
                                    <input
                                        type="date"
                                        value={dateInput}
                                        onChange={(e) => setDateInput(e.target.value)}
                                        min={new Date().toISOString().slice(0, 10)}
                                        className="w-full bg-black/30 border rounded-xl px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--gold)]"
                                        style={{ borderColor: "rgba(255,255,255,0.1)" }}
                                        data-testid="itinerary-date-input"
                                    />
                                </div>
                                <button onClick={saveDate} className="btn-gold text-sm !py-2 !px-4" data-testid="itinerary-date-save">{t("common.save")}</button>
                                <button onClick={() => setEditingDate(false)} className="btn-ghost text-sm !py-2 !px-3">{t("common.cancel")}</button>
                            </div>
                        )}
                    </div>
                )}

                {/* Concierge Chat */}
                {isOwner && <ConciergeChat itineraryId={id} />}

                <div className="mt-8 text-center text-white/40 text-xs">
                    {t("itinerary.craftedBy", { date: it.created_at ? `${t("itinerary.craftedByDatePrefix")}${new Date(it.created_at).toLocaleDateString()}` : "" })}
                </div>
            </div>
        </div>
    );
}

function NotFound({ msg, t }) {
    return (
        <div className="min-h-screen grid place-items-center text-center px-6">
            <div>
                <div className="font-serif text-4xl text-white mb-3">{t("itinerary.notAvailable")}</div>
                <div className="text-white/60 mb-6">{msg}</div>
                <Link to="/app" className="btn-gold text-sm !py-2.5 !px-5">{t("itinerary.backToPassport")}</Link>
            </div>
        </div>
    );
}
